import type { ReactElement } from "react"
import { useEffect, useRef } from "react"
import { Link as GatsbyLink } from "gatsby"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { SliderDataType } from "@gatsby-plugin-generic-page/fragments/sliderData"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import { Link as LinkUds } from "@ionos-web-design-system/react"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

type SliderSlideProps = SliderDataType & {
  isFirstSlide?: boolean
}

export default function SliderSlide({
  header,
  ctaElement,
  images,
  isReversed,
  isReversedOnMobile,
  link,
  isFirstSlide,
}: SliderSlideProps): ReactElement<SliderSlideProps> {
  const { currentPriceType } = usePriceTypeContext()
  const slideRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!link || !slideRef.current) return

    // ".focusable-slide-wrapper" is the class assigned by FullWidthSliderMol's Slide component
    // (@onestore/hel). The mol also queries a[data-section-link] on mount and sets tabindex="-1"
    // on it — this bidirectional contract means tabIndex={-1} on the sr-only anchors below is
    // intentional, matching what the mol will enforce anyway.
    const wrapper = slideRef.current.closest(".focusable-slide-wrapper")

    if (!wrapper) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const sectionLink = wrapper.querySelector("a[data-section-link]")

        if (sectionLink instanceof HTMLAnchorElement) {
          sectionLink.click()
        }
      }
    }

    wrapper.addEventListener("keydown", handleKeyDown)

    return () => wrapper.removeEventListener("keydown", handleKeyDown)
    // !!link: the handler only checks link existence, not its properties, so a boolean
    // dependency avoids re-attaching the listener when the parent re-renders with a new
    // object reference for the same link value.
  }, [!!link])

  const linkProps = link ? getLinkProps(link, currentPriceType) : null

  return (
    // TODO for now background is always bg-surface-subtlest, fix after ONESTORE-261
    <div
      ref={slideRef}
      className={cn(
        "bg-surface-subtlest w-full rounded-sm overflow-hidden",
        "flex md:items-center lg:gap-8 lg:px-16 min-h-[310px]",
        isReversedOnMobile ? "flex-col-reverse" : "flex-col",
        isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
      )}
    >
      {linkProps?.to ? (
        <GatsbyLink
          to={linkProps.to}
          title={linkProps.title}
          onClick={linkProps.onClick}
          className="sr-only"
          data-section-link
          tabIndex={-1}
        >
          {linkProps.title}
        </GatsbyLink>
      ) : linkProps?.href ? (
        <LinkUds
          href={linkProps.href}
          target={linkProps.openInNewTab ? "_blank" : undefined}
          rel={linkProps.openInNewTab ? "noopener noreferrer" : undefined}
          onClick={linkProps.onClick}
          title={linkProps.title}
          concept="mono"
          className="sr-only"
          data-section-link
          tabIndex={-1}
        >
          {linkProps.title}
        </LinkUds>
      ) : null}

      <div className="flex flex-col gap-4 p-4 lg:flex-1">
        <Header {...header} />

        {!isEmpty(ctaElement) ? (
          <div className="w-fit">
            <CtaElement ctaElement={ctaElement} />
          </div>
        ) : null}
      </div>

      <div className="w-full aspect-[16/10] lg:max-w-[500px] lg:max-h-[350px] flex items-center justify-center">
        <Media
          media={images}
          fetchPriority={isFirstSlide ? "high" : undefined}
        />
      </div>
    </div>
  )
}
